import React from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuItem, MenuList, MenuSeparator, MenuHeaderAction } from 'components/menu/items'
import { Tick } from 'components/icons'
import Tooltip from 'components/tooltip'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { ITableViewColumn } from 'types'

// How many of the view's columns stay in place on the left: none, or up to one of its first five
// columns on screen, named (as its header shows it), so it's plain which columns that takes. A view
// grouped as a tree always keeps its first column.

const MAX_FROZEN = 5

/**
 * The columns in the order the grid freezes them (see useColumnsViewport): the visible ones, with
 * grouped columns first, in group order. In a tree view the first slot is the tree column, which
 * takes the grouped columns and is headed by the first column after them.
 */
export const frozenOrder = (
  columns: ITableViewColumn[],
  hiddenColumnIds: string[],
  groupedColumnIds: string[],
  tree: boolean
): ITableViewColumn[] => {
  const visible = columns.filter((column) => !hiddenColumnIds.includes(column.publicId))
  const grouped = groupedColumnIds.flatMap((id) => visible.filter((column) => column.publicId === id))
  const others = visible.filter((column) => !groupedColumnIds.includes(column.publicId))
  if (tree && grouped.length > 0) return others.length > 0 ? others : grouped.slice(0, 1)
  return [...grouped, ...others]
}

const FreezeMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  const hasTreeColumn =
    spreadsheetData.viewDetails.treeGroupView && spreadsheetData.userConfiguration.groupSettings.length > 0
  // Nothing frozen reads as 0, whichever way it's stored (a view not yet loaded holds -1).
  const frozen = Math.max(0, spreadsheetData.viewDetails.frozenIndex)
  // A tree's first column always stays in place, so that's as far as clearing goes.
  const minFrozen = hasTreeColumn ? 1 : 0
  const choices = frozenOrder(
    spreadsheetData.viewDetails.columns,
    spreadsheetData.userConfiguration.hiddenColumns,
    spreadsheetData.userConfiguration.groupSettings.map((group) => group.columnId),
    hasTreeColumn
  ).slice(0, MAX_FROZEN)

  // Stays open, as a floating panel does on a pick, so a misclick is put right in place.
  const freeze = (frozenIndex: number) => {
    if (frozenIndex !== frozen) setSpreadsheetData({ type: 'CHANGE_FROZEN_INDEX', frozenIndex })
  }
  const tick = (count: number) => (count === frozen ? <Tick className="size-4 text-body" /> : undefined)
  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Freeze columns"
      action={
        frozen > minFrozen && (
          <MenuHeaderAction onClick={() => setSpreadsheetData({ type: 'CHANGE_FROZEN_INDEX', frozenIndex: minFrozen })}>
            Reset
          </MenuHeaderAction>
        )
      }
    >
      <MenuList>
        <div role="menu" aria-label="Freeze columns" className="flex flex-col">
          <MenuItem
            role="menuitemradio"
            aria-checked={frozen === 0}
            disabledReason={hasTreeColumn ? "A tree's first column always stays in place" : undefined}
            onClick={() => freeze(0)}
            end={tick(0)}
          >
            {"Don't freeze any columns"}
          </MenuItem>
          <MenuSeparator />
          {choices.map((column, index) => (
            <MenuItem
              key={column.publicId}
              role="menuitemradio"
              aria-checked={frozen === index + 1}
              onClick={() => freeze(index + 1)}
              end={tick(index + 1)}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="shrink-0">Up to</span>{' '}
                {/* The column as the grid's headers show it: its 14px icon and its name in semibold. */}
                <span className="flex min-w-0 items-center gap-1.5 font-semibold [&_svg]:size-3.5">
                  <IconSelector kind={column.kind} />
                  <Tooltip content={column.name} showOnlyIfTruncated>
                    <span className="min-w-0 truncate">{column.name}</span>
                  </Tooltip>
                </span>
              </span>
            </MenuItem>
          ))}
        </div>
      </MenuList>
    </FloatingMenu>
  )
}

export default FreezeMenu
